import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  HardDrive,
  Smartphone,
  Tablet,
  Laptop,
  LogOut,
  AlertCircle,
  CheckCircle2,
  MapPin,
  Clock,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import SidebarLayout from '../components/SidebarLayout';
import fileService from '../services/fileService';
import { formatFileSize } from '../utils/helpers';
import { cn } from '../lib/utils';

// Shadcn UI Components
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card"
import { Progress } from "../components/ui/progress"
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert"
import { Badge } from "../components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog"

const Settings = () => {
  // Storage info
  const [storageInfo, setStorageInfo] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(true);

  // Active sessions
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [logoutAllDialogOpen, setLogoutAllDialogOpen] = useState(false);

  // States
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadStorageInfo();
    loadActiveSessions();
  }, []);

  // ============================================
  // 1. STORAGE INFO
  // ============================================
  const loadStorageInfo = async () => {
    try {
      setLoadingStorage(true);
      const response = await fileService.getStorageStats();
      if (response.success) {
        setStorageInfo(response.storage);
      }
    } catch (err) {
      console.error('Error loading storage:', err);
      setError('Gagal memuat informasi penyimpanan');
    } finally {
      setLoadingStorage(false);
    }
  };

  const getStoragePercentage = () => {
    if (!storageInfo) return 0;
    return (storageInfo.used / storageInfo.limit) * 100;
  };

  // ============================================
  // 2. ACTIVE SESSIONS
  // ============================================
  const loadActiveSessions = async () => {
    try {
      setLoadingSessions(true);

      // Get current session info from browser
      const loginTime = localStorage.getItem('loginTime');

      // Detect browser and OS logic (simplified for implementation)
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown Browser';
      let osName = 'Unknown OS';
      let deviceType = 'laptop';

      if (userAgent.indexOf('Chrome') > -1) browserName = 'Chrome';
      else if (userAgent.indexOf('Safari') > -1) browserName = 'Safari';
      else if (userAgent.indexOf('Firefox') > -1) browserName = 'Firefox';

      if (userAgent.indexOf('Win') > -1) osName = 'Windows';
      else if (userAgent.indexOf('Mac') > -1) osName = 'macOS';
      else if (userAgent.indexOf('Linux') > -1) osName = 'Linux';
      else if (userAgent.indexOf('Android') > -1) { osName = 'Android'; deviceType = 'phone'; }
      else if (userAgent.indexOf('iPhone') > -1) { osName = 'iOS'; deviceType = 'phone'; }

      const currentSession = {
        id: 1,
        device: `${browserName} on ${osName}`,
        deviceType: deviceType,
        ip: '103.133.36.140', // Mock IP
        location: 'Indonesia',
        lastActive: loginTime || new Date().toISOString(),
        isCurrent: true
      };

      setSessions([currentSession]);

    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Gagal memuat sesi aktif');
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleRemoveSession = async (sessionId) => {
    // Mock remove session
    setSessions(sessions.filter(s => s.id !== sessionId));
    setSuccess('Sesi berhasil dihapus');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleLogoutAll = async () => {
    try {
      setLogoutAllDialogOpen(false);
      setSuccess('Berhasil keluar dari semua perangkat. Mengalihkan...');
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError('Gagal keluar dari semua perangkat');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'phone': return <Smartphone className="h-5 w-5" />;
      case 'tablet': return <Tablet className="h-5 w-5" />;
      default: return <Laptop className="h-5 w-5" />;
    }
  };

  const formatLastActive = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit yang lalu`;
    return date.toLocaleDateString('id-ID', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <SidebarLayout storageData={storageInfo ? { storage: storageInfo } : null}>
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <SettingsIcon className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              Pengaturan
            </h1>
            <p className="text-muted-foreground">
              Kelola penyimpanan dan keamanan akun Anda.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-500 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Berhasil</AlertTitle>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column (Main Content) */}
          <div className="md:col-span-2 space-y-6">

            {/* Storage Management */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg dark:bg-blue-900 dark:text-blue-300">
                    <HardDrive className="h-5 w-5" />
                  </div>
                  <CardTitle>Manajemen Penyimpanan</CardTitle>
                </div>
                <CardDescription>Status penggunaan penyimpanan akun Anda.</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingStorage ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : storageInfo ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <div className="text-2xl font-bold text-primary">{formatFileSize(storageInfo.used)}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">Terpakai</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <div className="text-2xl font-bold">{formatFileSize(storageInfo.limit)}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">Total</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatFileSize(storageInfo.available)}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">Tersedia</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <div className="text-2xl font-bold">{storageInfo.files_count}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">Files</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Penggunaan</span>
                        <span className="font-medium">{getStoragePercentage().toFixed(1)}%</span>
                      </div>
                      <Progress
                        value={getStoragePercentage()}
                        className="h-3"
                        indicatorClassName={getStoragePercentage() > 90 ? "bg-red-500" : getStoragePercentage() > 75 ? "bg-yellow-500" : "bg-primary"}
                      />
                    </div>

                    {getStoragePercentage() > 80 && (
                      <Alert variant="warning" className="mt-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Penyimpanan Hampir Penuh</AlertTitle>
                        <AlertDescription>
                          Anda telah menggunakan lebih dari 80% kapasitas penyimpanan. Hapus file yang tidak perlu untuk mengosongkan ruang.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert variant="destructive">Gagal memuat data penyimpanan.</Alert>
                )}
              </CardContent>
            </Card>

            {/* Active Sessions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 text-purple-600 rounded-lg dark:bg-purple-900 dark:text-purple-300">
                      <Laptop className="h-5 w-5" />
                    </div>
                    <CardTitle>Sesi Aktif</CardTitle>
                  </div>
                  {sessions.length > 1 && (
                    <Button variant="destructive" size="sm" onClick={() => setLogoutAllDialogOpen(true)}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Log Out Semua
                    </Button>
                  )}
                </div>
                <CardDescription>Perangkat yang sedang login ke akun Anda.</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSessions ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : sessions.length > 0 ? (
                  <div className="space-y-4">
                    {sessions.map((session) => (
                      <div key={session.id} className={cn(
                        "flex items-start justify-between p-4 rounded-lg border",
                        session.isCurrent ? "bg-primary/5 border-primary/20" : "bg-card"
                      )}>
                        <div className="flex items-start gap-4">
                          <div className={cn(
                            "p-3 rounded-full flex items-center justify-center",
                            session.isCurrent ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          )}>
                            {getDeviceIcon(session.deviceType)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-sm">{session.device}</h4>
                              {session.isCurrent && <Badge>Saat Ini</Badge>}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {session.location} ({session.ip})
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {formatLastActive(session.lastActive)}
                              </div>
                            </div>
                          </div>
                        </div>
                        {!session.isCurrent && (
                          <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => handleRemoveSession(session.id)}>
                            <LogOut className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}

                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Info Keamanan</AlertTitle>
                      <AlertDescription className="text-xs">
                        Jika Anda melihat perangkat yang tidak dikenal, segera log out dari semua sesi dan ganti password Anda.
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">Tidak ada sesi aktif.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column (Summary) */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ringkasan Akun</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Total File</div>
                  <div className="text-3xl font-bold">{storageInfo?.files_count || 0}</div>
                </div>
                <div className="h-px bg-border my-2" />
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Perangkat Aktif</div>
                  <div className="text-3xl font-bold">{sessions.length}</div>
                </div>
                <div className="h-px bg-border my-2" />
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Kesehatan Storage</div>
                  <Badge variant={getStoragePercentage() > 80 ? "destructive" : "outline"} className={getStoragePercentage() > 80 ? "" : "bg-green-50 text-green-700 border-green-200"}>
                    {getStoragePercentage() > 80 ? "Perlu Perhatian" : "Sehat"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Logout All Dialog */}
      <Dialog open={logoutAllDialogOpen} onOpenChange={setLogoutAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Out Semua Perangkat?</DialogTitle>
            <DialogDescription>
              Tindakan ini akan mengeluarkan akun Anda dari semua perangkat yang sedang login, termasuk perangkat ini. Anda harus login kembali.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogoutAllDialogOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleLogoutAll}>Ya, Log Out Semua</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default Settings;